import React, { useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import ApiService from '../ApiService'
import { Button, Col, Container, Row, Spinner, Form } from 'react-bootstrap'
import MaterialTable from 'material-table'
import { IoSearchOutline } from 'react-icons/io5'
import DatePicker from 'react-datepicker'
import removeClass from '../util/RemoveCSS'
import PageTitle from './Common/PageTitle/PageTitle'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import Snackbar from '@material-ui/core/Snackbar'
import MuiAlert from '@material-ui/lab/Alert'
import { parseAxiosError } from '../util/parserHelper'
import { momentFutureDate, momentPastDate } from '../util/common'
import { DateTime } from 'luxon'
import {BOOKING_STATUS_CONFIG} from "../util/bookingUtils";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const BookingSearchType = {
  DATE_RANGE: 'DATE_RANGE',
  LASTNAME_DOB: 'LASTNAME_DOB',
  BOOKING_ID: 'BOOKING_ID',
  MOBILE: 'MOBILE',
  EMAIL: 'EMAIL',
  UNASSIGNED: 'UNASSIGNED',
  SHOW_TODAY_BOOKINGS: 'SHOW_TODAY_BOOKINGS',
  BOOKING_STATUS: 'BOOKING_STATUS',
}

const jsNowMinus14Days = momentPastDate(14).toDate()
const jsNowPlus1Days = momentFutureDate(1).toDate()
const BACKEND_DATE_FORMAT = 'yyyy-MM-dd'
const BOOKING_DETAIL_PATH = 'BookingDetails'

const BookingList = () => {
  const [cachedBookingResults, setCachedBookingResults] = useState(null)
  const [bookingListForRender, setBookingListForRender] = useState([])
  const [calData, setCalData] = useState(null)
  const [errors, setErrors] = useState([])
  const [isRemoteCallInProgress, setRemoteCallInProgress] = useState(true)
  const [loading, setLoading] = useState(true)
  const navigate = useHistory()

  const [searchType, setSearchType] = useState(BookingSearchType.DATE_RANGE)
  const [searchDob, setSearchDob] = useState(null)
  const [searchLastName, setSearchLastName] = useState(null)
  const [searchLnameDate, setSearchLnameDate] = useState(new Date())
  const [searchFromDate, setSearchFromDate] = useState(jsNowMinus14Days)
  const [searchToDate, setSearchToDate] = useState(jsNowPlus1Days)
  const [searchBookingId, setSearchBookingId] = useState(null)
  const [searchMobile, setSearchMobile] = useState(null)
  const [searchEmail, setSearchEmail] = useState(null)
  const [searchStatus, setSearchStatus] = useState(null)
  const [snackOpen, setSnackOpen] = useState(false)
  const [snackText, setSnackText] = useState('')
  const [snackSeverity, setSnackSeverity] = useState('')
  const [errSnackData, setErrSnackData] = useState(null)
  const [successSnackData, setSuccessSnackData] = useState(null)
  const [isShowOnlyMyBookings, setIsShowOnlyMyBookings] = useState(null)

  const resetSnackMsg = () => {
    setSuccessSnackData(null)
    setErrSnackData(null)
  }

  useEffect(() => {
    if(searchFromDate && (!searchToDate || searchToDate < searchFromDate || moment(searchToDate).diff(moment(searchFromDate), 'days') > 15)){
      setSearchToDate(moment(searchFromDate).add(15, 'days').toDate());
    }
  }, [searchFromDate]);


  useEffect(() => {
    if (errSnackData?.message) {
      handleSnackOpen(errSnackData.message, 'error')
    }
  }, [errSnackData])

  useEffect(() => {
    if (successSnackData?.message) {
      handleSnackOpen(successSnackData.message, 'success')
    }
  }, [successSnackData])

  const handleSnackOpen = (text, severity) => {
    setSnackSeverity(severity)
    setSnackText(text)
    setSnackOpen(true)
  }

  const handleSnackClose = () => {
    setSnackOpen(false)
    resetSnackMsg()
  }

  const handleSelectEvent = event => {
    navigate.push({
      pathname: BOOKING_DETAIL_PATH,
      search: '?bookingId=' + event.bookingId,
    })
  }

  const doSearchForBookings = async () => {
    setLoading(true)
    resetSnackMsg()
    try {
      ;(async () => {
        await searchForBookingCommon(searchType).then(r => r)
      })()
    } catch (err) {
      setErrSnackData({ message: `find booking failed, reason: ${err.message}` })
      setCachedBookingResults([])
    } finally {
      setLoading(false)
    }
  }

  const searchForBookingCommon = async type => {
    resetSnackMsg();
    if(type === BookingSearchType.DATE_RANGE) {
      const luxonFromDate = DateTime.fromJSDate(searchFromDate);
      const luxonToDate = DateTime.fromJSDate(searchToDate);
      const diffInDays = luxonFromDate.diff(luxonToDate, 'days').toObject().days;
      const diffInDaysAbs = Math.abs(diffInDays);
      if(diffInDaysAbs > 16) {
        setLoading(false);
        setRemoteCallInProgress(false);
        setErrSnackData({ message: `Date range cannot be more than 15 days` });
        return;
      }
    }
    setCachedBookingResults([])
    setRemoteCallInProgress(true)
    await searchBookingFetcher(type)
      .then(res => {
        res?.data ? setCachedBookingResults(res.data) : setCachedBookingResults([])
        handleSnackOpen('Booking(s) found', 'success')
        return
      })
      .catch(err => {
        setErrSnackData({ message: `Booking search failed. Reason: ${parseAxiosError(err)?.reason || 'unknown'}` })
        setCachedBookingResults([])
      })
      .finally(() => {
        setRemoteCallInProgress(false)
      })
  }

  const getBkendFormatted = jsDate => {
    return DateTime.fromJSDate(jsDate).toFormat(BACKEND_DATE_FORMAT)
  }

  const searchBookingFetcher = async type => {
    switch (type) {
      case BookingSearchType.BOOKING_ID:
        return ApiService.getAllBookingsById(searchBookingId)
      case BookingSearchType.LASTNAME_DOB:
        return ApiService.getAllBookingsForLastname(searchLastName, getBkendFormatted(searchLnameDate))
      case BookingSearchType.DATE_RANGE:
        return ApiService.getAllBookingsForDateRange(getBkendFormatted(searchFromDate), getBkendFormatted(searchToDate))
      case BookingSearchType.EMAIL:
        return ApiService.getAllBookingsByEmail(searchEmail)
      case BookingSearchType.MOBILE:
        return ApiService.getAllBookingsByMobile(searchMobile)
      case BookingSearchType.SHOW_TODAY_BOOKINGS:{
        return ApiService.getAllBookingsForDateRange(getBkendFormatted(DateTime.now().toJSDate()), getBkendFormatted(DateTime.now().toJSDate()))
      }
      case BookingSearchType.UNASSIGNED: {
        const allBookings = cachedBookingResults.filter(b => b.isUnAssigned === true)
        return Promise.resolve({ data: allBookings })
      }
      case BookingSearchType.BOOKING_STATUS: {
        DateTime.fromJSDate(searchFromDate).toFormat(BACKEND_DATE_FORMAT)
        const res = await ApiService.getAllBookingsForDateRange(getBkendFormatted(searchFromDate), getBkendFormatted(searchToDate))
        const allBookings = res?.data?.filter(b => b.bookingStatus === searchStatus)
          return Promise.resolve({ data: allBookings })
      }
      default:
        break
    }
    throw new Error(`Unsupported search type: ${searchType}`)
  }

  const navigateToDetailPage = bookId => {
    navigate.push({
      pathname: BOOKING_DETAIL_PATH,
      search: '?bookingId=' + bookId,
    })
  }

  const assignToMe = async bookingId => {
    resetSnackMsg()
    setLoading(true)
    setRemoteCallInProgress(true)
    await ApiService.assignToMe(bookingId)
      .then(resp => {
        setSuccessSnackData({ message: 'Booking assigned to you,navigating to detail page' })
        navigateToDetailPage(bookingId)
      })
      .catch(ex => setErrSnackData({ message: `Failed to assign booking to you. Reason: ${parseAxiosError(ex)?.reason || 'unknown'}` }))
      .finally(() => {
        setLoading(false)
        setRemoteCallInProgress(false)
      })
  }

  // get all cachedBookingResults list
  async function initialBookingLoad() {
    setSearchType(BookingSearchType.DATE_RANGE)
    setSearchFromDate(jsNowMinus14Days)
    setSearchToDate(jsNowPlus1Days)
    doSearchForBookings()
  }

  useEffect(() => {
    ;(async () => {
      await initialBookingLoad().then(r => r)
    })()
  }, [])

  //myBookingsOnly will only be recalculated if either cachedBookingResults or isShowOnlyMyBookings changes.
  const refreshedAllResults = useMemo(() => {
    return cachedBookingResults?.filter(res => !isShowOnlyMyBookings || res?.isAssignedToMe === true)
  }, [cachedBookingResults, isShowOnlyMyBookings])

  useEffect(() => {
    setBookingListForRender(refreshedAllResults)
  }, [refreshedAllResults])

  //navigate to /BookingDetails
  const navigateToBookingDetails = id => {
    setLoading(true)
    try {
      navigate.push({
        pathname: BOOKING_DETAIL_PATH,
        search: '?bookingId=' + id,
      })
    } catch (err) {
      setLoading(false)
      setErrSnackData({ message: 'failed navigating to booking detail page' })
    }
  }
  removeClass()

  function renderErrors() {
    return errors.map((k, i) => {
      return (
        <li style={{ color: 'red' }} key={k}>
          {k}
        </li>
      )
    })
  }

  return (
    <Container fluid>
      <PageTitle titleName={'Booking List'} />
      <Container fluid={'lg'}>
        {loading || isRemoteCallInProgress ? (
          <Row>
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </Row>
        ) : (
          <>
            <Row>
              <Col>{renderErrors()}</Col>
            </Row>
            {/* search section */}
            <Row>
              <div className="Search-wrap" style={{ justifyContent: 'space-between', padding: '15px' }}>
                <Form style={{ width: '100%' }}>
                  <Row style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Select
                          sm={6}
                          id="formSelect"
                          value={searchType}
                          onChange={e => {
                            setSearchType(e.target.value)
                          }}
                        >
                          <option value={BookingSearchType.SHOW_TODAY_BOOKINGS}>Show today's Bookings</option>
                          <option value={BookingSearchType.BOOKING_ID}>Search by Booking id</option>
                          <option value={BookingSearchType.LASTNAME_DOB}>Search by Last name & DOB</option>
                          <option value={BookingSearchType.DATE_RANGE}>Search by Date range</option>
                          <option value={BookingSearchType.MOBILE}>Search by mobile</option>
                          <option value={BookingSearchType.EMAIL}>Search by email</option>
                          <option value={BookingSearchType.UNASSIGNED}>View Unassigned Bookings</option>
                          <option value={BookingSearchType.BOOKING_STATUS}>Search by Booking Status</option>

                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs="auto">
                      {/* if search for selected show the respective input form */}
                      {searchType === BookingSearchType.BOOKING_ID ? (
                        <Form.Group as={Col}>
                          <Form.Control placeholder="Enter booking id" type="text" onChange={e => setSearchBookingId(e.target.value)} />
                        </Form.Group>
                      ) : searchType === BookingSearchType.LASTNAME_DOB ? (
                        <Form.Group as={Col}>
                          <Row>
                            <Col>
                              <Form.Control placeholder="Enter Last name" type="text" onChange={e => setSearchLastName(e.target.value)} />
                            </Col>
                            <Col>
                              <DatePicker dateFormat={'MM/dd/yyyy'} selected={searchLnameDate} onChange={setSearchLnameDate} placeholderText="DOB" />
                            </Col>
                          </Row>
                        </Form.Group>
                      ) : searchType === BookingSearchType.DATE_RANGE ? (
                        <Row>
                          <Col>
                            <DatePicker dateFormat={'MM/dd/yyyy'} selected={searchFromDate} onChange={setSearchFromDate} placeholderText="From" />
                          </Col>
                          <Col>
                            <DatePicker dateFormat={'MM/dd/yyyy'} selected={searchToDate} onChange={setSearchToDate} placeholderText="To" />
                          </Col>
                        </Row>
                      ) : searchType === BookingSearchType.MOBILE ? (
                        <Row>
                          <Col>
                            <Form.Control placeholder="Enter patient mobile" type="text" onChange={e => setSearchMobile(e.target.value)} />
                          </Col>
                        </Row>
                      ) : searchType === BookingSearchType.EMAIL ? (
                        <Row>
                          <Col>
                            <Form.Control placeholder="Enter patient email" type="text" onChange={e => setSearchEmail(e.target.value)} />
                          </Col>
                        </Row>
                        ) : searchType === BookingSearchType.BOOKING_STATUS ? (
                        <Row>
                          <Col>
                          <Form.Group>
                            <Form.Select sm={6} id="formSelect" value={searchStatus} onChange={e => setSearchStatus(e.target.value)}>
                              <option value="" disabled>Select</option>
                              {BOOKING_STATUS_CONFIG.map((config, index) => (
                                  <option key={index} value={config.status}>{config.display}</option>
                              ))}
                            </Form.Select>

                          </Form.Group>
                          </Col>
                        </Row>
                      ) : null}
                    </Col>
                    <Col xs="auto">
                      <Button size="md" onClick={doSearchForBookings}>
                        <IoSearchOutline class="" /> Search Now
                      </Button>
                    </Col>
                    <Col>
                      <div className="clear-all-filters" style={{ textAlign: 'right' }}>
                        <Button size="md" onClick={initialBookingLoad}>
                          <IoSearchOutline class="" /> Clear all Filters
                        </Button>
                      </div>
                      {/*<div className="clear-all-filters" style={{ textAlign: 'right' }}>*/}
                      {/*  <a href="javascript:void(0);" onClick={initialBookingLoad}>*/}
                      {/*    Clear all Filters*/}
                      {/*  </a>*/}
                      {/*</div>*/}
                    </Col>
                  </Row>
                </Form>
              </div>
            </Row>

            <Row className="mt-2">
              <MaterialTable
                columns={[
                  {
                    title: 'Booking id',
                    field: 'id',
                    render: rowData => (
                      <Button variant="link" onClick={() => navigateToBookingDetails(rowData.id)}>
                        {rowData.id}
                      </Button>
                    ),
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'Assign to me',
                    field: 'assign',
                    render: rowData => (
                      <Button style={{ width: 'auto' }} variant="primary" disabled={rowData?.isAssignedToMe === true} onClick={() => assignToMe(rowData.id)}>
                        {rowData?.isAssignedToMe === true ? 'Assigned to you' : rowData?.primaryClinicianId ? 'Re-assign to me' : 'Assign to me'}
                      </Button>
                    ),
                    cellStyle: {
                      width: 200,
                      minWidth: 200,
                    },
                    headerStyle: {
                      width: 200,
                      minWidth: 200,
                    },
                  },
                  {
                    title: 'First name',
                    field: 'firstName',
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'Last name',
                    field: 'lastName',
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'Care Type/Location',
                    field: 'careType',
                    render: rowData => (
                      <div>
                        <div>{rowData.careType?.displayName || 'unknown'}</div>
                        <div>{rowData.careLocation?.address || 'unknown'}</div>
                      </div>
                    ),
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'Primary Clinician',
                    field: 'primaryClinicianName',
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'Contact',
                    field: 'contact',
                    render: rowData => (
                      <div>
                        <div>{rowData?.email ?? 'no email'}</div>
                        <div>{rowData?.mobile ?? rowData?.phone ?? 'no phone'}</div>
                      </div>
                    ),
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'DOB',
                    field: 'dob',
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'Appointment',
                    field: 'appntStart',
                    render: rowData => (
                      <div>
                        {moment(rowData.appntStart)
                          .local()
                          .format('DD MMM YY')}
                        &nbsp;at&nbsp;
                        {moment(rowData.appntStart)
                          .local()
                          .format('hh:mm A')}
                      </div>
                    ),
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'City',
                    field: 'city',
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'State',
                    field: 'state',
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                  {
                    title: 'Status',
                    field: 'bookingStatus',
                    cellStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                    headerStyle: {
                      width: 150,
                      minWidth: 150,
                    },
                  },
                ]}
                data={bookingListForRender}
                options={{
                  filtering: true,
                  exportButton: true,
                  headerStyle: {
                    position: 'sticky',
                    top: 0,
                    color: 'black',
                  },
                  maxBodyHeight: '630px',
                  search: true,
                  pageSize: 100,
                  pageSizeOptions: [50, 100, 200, 500],
                  emptyRowsWhenPaging: false,
                  draggable: false,
                  showTitle: true,
                  toolbarButtonAlignment: 'right',
                  exportAllData: true,
                  doubleHorizontalScroll: true,
                  sorting: true,
                }}
                actions={[
                  {
                    icon: () => (
                      <Button
                        variant="light"
                        style={{ width: 'auto', color: 'white', background: '#866BD9', border: 0 }}
                        size="md"
                        onClick={() => setIsShowOnlyMyBookings(!isShowOnlyMyBookings)}
                      >
                        {isShowOnlyMyBookings ? 'Show All Bookings' : 'Show Only My Bookings'}
                      </Button>
                    ),
                    tooltip: 'Filter',
                    onClick: (event, rowData) => setIsShowOnlyMyBookings(!isShowOnlyMyBookings),
                    isFreeAction: true,
                  },
                ]}
                title="Available Bookings"
              />
            </Row>
          </>
        )}
      </Container>
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default BookingList
